import { RESOURCE_TYPE_FILE, RESOURCE_TYPE_MERGED_FILE } from "@Features/transmittals/hooks/transmittal.types";

/**
 * The two fields this resolver reads off a `filesData` entry, mirroring `FileVersionData`.
 * Callers hold that slice under several different local declarations (Gantt's own `RootState`
 * narrows it to `{ fileId }`), so the parameter stays `object` and the entry is viewed through
 * this alias — an all-optional parameter type would trip TS's weak-type check on those callers.
 */
interface FilesDataEntry {
  resourceType?: string;
  sourceFileVersionId?: { id: string; isDeleted?: number }[];
}

/**
 * Expands a viewer's `fileVersionId` into the list of fileVersionIds whose model-properties we want to load.
 *   RESOURCE_TYPE_FILE         → [fileVersionId]
 *   RESOURCE_TYPE_MERGED_FILE  → non-deleted sourceFileVersionId[].id
 * Same branching the viewer uses when dispatching the initial `getModelProperties`.
 */
export function resolveFileVersionIds(
  // The `filesData` redux slice, keyed by fileVersionId (see reducers/fileVersion.ts).
  filesData: Record<string, object> | undefined,
  // Callers pass the viewer's currently-open file version, which is absent when no file is
  // mounted; the `!fileVersionId` guard below has always handled that.
  fileVersionId: string | undefined,
): string[] {
  if (!fileVersionId) return [];
  const entry = filesData?.[fileVersionId] as FilesDataEntry | undefined;
  if (!entry) return [fileVersionId];

  if (entry.resourceType === RESOURCE_TYPE_FILE) {
    return [fileVersionId];
  }
  if (entry.resourceType === RESOURCE_TYPE_MERGED_FILE) {
    return (
      (entry.sourceFileVersionId ?? [])
        // Element type comes from `SourceFileVersionData`, whose `isDeleted` is a NUMBER flag,
        // not a boolean — the previous local annotation said boolean and was wrong.
        .filter((sf) => !sf.isDeleted)
        .map((sf) => sf.id)
    );
  }
  return [fileVersionId];
}
